import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { 
  LayoutDashboard, 
  Settings as SettingsIcon, 
  Upload, 
  Wrench, 
  History,
  Menu,
  X
} from "lucide-react";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/settings", icon: SettingsIcon, label: "Settings" },
  { to: "/import", icon: Upload, label: "Import Baseline" },
  { to: "/test-tools", icon: Wrench, label: "Test Tools" },
  { to: "/history", icon: History, label: "Sync History" },
];

export const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-screen flex flex-col border-r border-stone-800 z-50
        bg-stone-900 text-stone-200 w-64
        transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Noise texture overlay */}
        <div className="absolute inset-0 sidebar-texture pointer-events-none" />
        
        {/* Logo */}
        <div className="relative px-6 py-6 border-b border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_87448469-894d-4c78-80bf-5af75d5046a1/artifacts/kd30d3ib_logo.png" 
              alt="Rolling River Day Camp" 
              className="w-10 h-10 rounded-lg object-cover"
            />
            <div>
              <h1 className="font-heading font-bold text-white text-lg leading-tight">Rolling River</h1>
              <p className="text-xs text-stone-400 tracking-wide">Camper ID</p>
            </div>
          </div>
          <button 
            className="lg:hidden text-stone-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="relative flex-1 py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-4 transition-colors border-l-4 ${
                  isActive
                    ? "border-forest-light bg-stone-800 text-white"
                    : "border-transparent hover:bg-stone-800 hover:text-white hover:border-forest-light/50"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="relative px-6 py-4 border-t border-stone-800">
          <p className="text-xs text-stone-500">Season 2026</p>
          <p className="text-xs text-stone-600 mt-1">America/New_York</p>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 lg:ml-64 min-h-screen">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-30 bg-stone-900 px-4 py-3 flex items-center gap-3 border-b border-stone-800">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-white"
            data-testid="open-sidebar-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <img 
            src="https://customer-assets.emergentagent.com/job_87448469-894d-4c78-80bf-5af75d5046a1/artifacts/kd30d3ib_logo.png" 
            alt="Rolling River" 
            className="w-8 h-8 rounded-lg object-cover"
          />
          <span className="font-heading font-bold text-white">Rolling River Camper ID</span>
        </div>

        <div className="p-4 sm:p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
